'use client';
import { Trash02 } from '@ethsign/icons';

import { Button, Modal, ScrollArea, useToast } from '@ethsign/ui';
import { useMutation, useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Card, ColorRemoveIcon, Header } from '@/components';
import { Spin } from '@/components/Loading';
import { useRouter, useUserInfo } from '@/hooks';
import { deleteAdminTemplate, queryAdminTemplate, queryAdminTemplates, updateTemplateOperation } from '@/services';
import { ITemplate, SendType, SIGN_MODE_ENUM } from '@/types';

import { ContractTemplateCard } from '../components/ContractTemplateCard';

const RemoveTemplateModal = ({ id, onSuccess }: { id: number; onSuccess: () => void }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { mutateAsync, isPending: loading } = useMutation({
    mutationFn: deleteAdminTemplate,
    mutationKey: ['deleteAdminTemplate']
  });
  const handleConfirm = async () => {
    await mutateAsync(String(id));
    toast({
      title: 'Success',
      description: 'Template deleted',
      variant: 'success'
    });
    onSuccess();
    setOpen(false);
  };
  return (
    <>
      <Modal
        open={open}
        onOpenChange={setOpen}
        trigger={
          <span className={'absolute right-2 top-2 z-10'}>
            <Trash02 color={'#98A2B3'} />
          </span>
        }
        onCancel={() => setOpen(false)}
        onConfirm={handleConfirm}
        confirmText={'Delete'}
        confirmButtonProps={{
          loading
        }}
        className={'md:w-[400px]'}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorRemoveIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Remove Template</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center">
            Are you sure you want to delete this template? This action can not be undone.
          </div>
        </div>
      </Modal>
    </>
  );
};

const TemplateActionModal = ({ data, onSuccess }: { data: any; onSuccess: () => void }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { mutateAsync, isPending: loading } = useMutation({
    mutationFn: updateTemplateOperation,
    mutationKey: ['updateTemplateOperation']
  });
  const handleConfirm = async () => {
    await mutateAsync({
      id: data.id,
      action: data.hidden ? 'unhide' : 'hide'
    });
    toast({
      title: 'Success',
      description: `Template ${data.hidden ? 'public' : 'hide'} success`,
      variant: 'success'
    });
    onSuccess();
    setOpen(false);
  };
  return (
    <>
      <Modal
        open={open}
        onOpenChange={setOpen}
        trigger={
          <Button size={'sm'} className={'flex-1'} variant={!data.hidden ? 'destructive' : 'primary'}>
            {data.hidden ? 'Public' : 'Hide'}
          </Button>
        }
        onCancel={() => setOpen(false)}
        onConfirm={handleConfirm}
        confirmText={'Confirm'}
        confirmButtonProps={{
          loading,
          variant: 'primary'
        }}
        className={'md:w-[400px]'}
      >
        <div className="flex items-center flex-col justify-center">
          <div className="text-lg font-medium text-gray-900 mt-[20px]">{data.hidden ? 'Public' : 'Hide'} Template</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center">
            Are you sure you want to {data.hidden ? 'public' : 'hide'} this template?
          </div>
        </div>
      </Modal>
    </>
  );
};

const Templates = () => {
  const { user } = useUserInfo();
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['admin-template'],
    queryFn: () => queryAdminTemplates({ size: 100 })
  });
  const router = useRouter();

  const handleCreate = (sendType: SendType) => {
    const id = `temp${Date.now()}`;
    router.push(`/console/template/create/${id}?sendType=${sendType}`);
  };

  const handleEdit = (item: ITemplate) => {
    let sendType = '';
    const tagArr = item.tags?.split('、');
    if (tagArr?.includes(SendType.invite)) {
      sendType = SendType.invite;
    } else {
      if (tagArr?.includes(SIGN_MODE_ENUM.OneSignOne) || tagArr?.includes(SIGN_MODE_ENUM.AllSignOne)) {
        sendType = SendType.link;
      }
    }
    router.push(`/console/template/${item.id}?sendType=${sendType}`);
  };

  return (
    <div className="w-full h-full bg-background">
      <Header user={user} />
      <div className="pt-[72px] h-full ">
        <Card
          title={'Contract Template'}
          className={'m-5'}
          extra={
            <div className="flex gap-2">
              <Button onClick={() => handleCreate(SendType.invite)}>Create Invite Mode Template</Button>
              <Button onClick={() => handleCreate(SendType.link)}>Create Link Mode Template</Button>
            </div>
          }
        >
          <Spin loading={isFetching}>
            <ScrollArea className={'h-[calc(100vh-160px)]'}>
              <div className="w-full p-8 grid grid-cols-6 gap-6">
                {data?.rows?.map((item) => (
                  <ContractTemplateCard
                    key={item.id}
                    footer={
                      <div className={'w-full mt-2 flex gap-2'}>
                        <Button size={'sm'} variant={'outline'} className={'flex-1'} onClick={() => handleEdit(item)}>
                          Edit
                        </Button>
                        <TemplateActionModal data={item} onSuccess={refetch} />
                      </div>
                    }
                    data={item}
                    action={<RemoveTemplateModal id={item.id!} onSuccess={refetch} />}
                    service={queryAdminTemplate}
                  />
                ))}
              </div>
            </ScrollArea>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default Templates;
